import { parseCourierSetupDraft } from "./courierSetup";
import { parseSalonSetupDraft } from "./salonSetup";
import { parsePersonalTrainerSetupDraft } from "./personalTrainerServices";
import type { IndustrySetupInput } from "./types";

export type IndustrySetupDraft = {
  workspaceUuid: string;
  workspaceName: string;
  stage: "workspace" | "setup";
  setupStep?: number;
  input: IndustrySetupInput;
};

export function canResumeIndustrySetupDraft(
  draft: IndustrySetupDraft,
  workspaces: readonly { uuid: string }[],
): boolean {
  return draft.workspaceUuid === ""
    ? draft.stage === "workspace"
    : workspaces.some((workspace) => workspace.uuid === draft.workspaceUuid);
}

const selectionFields = [
  "companyUuid", "branchUuid", "coachProfileUuid", "bookingPortalUuid",
  "bookingResourceUuid", "bookingServiceUuid", "clientPortalUuid",
] as const;

export function parseIndustrySetupDraft(raw: string | null): IndustrySetupDraft | null {
  try {
    const value: unknown = JSON.parse(raw ?? "null");
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const draft = value as Record<string, unknown>;
    if (
      typeof draft.workspaceUuid !== "string"
      || typeof draft.workspaceName !== "string"
      || (draft.stage !== "workspace" && draft.stage !== "setup")
      || !draft.input || typeof draft.input !== "object" || Array.isArray(draft.input)
    ) return null;

    const data = draft.input as Record<string, unknown>;
    if (typeof data.displayName !== "string") return null;
    const input: IndustrySetupInput = {
      displayName: data.displayName.slice(0, 255),
      defaultDurationMinutes: typeof data.defaultDurationMinutes === "number"
        ? data.defaultDurationMinutes
        : 60,
      installOnly: data.installOnly === true,
      salonSetup: parseSalonSetupDraft(data.salonSetup),
      personalTrainerSetup: parsePersonalTrainerSetupDraft(data.personalTrainerSetup),
      courierSetup: parseCourierSetupDraft(data.courierSetup),
      selectedRecommendedAppCodes: Array.isArray(data.selectedRecommendedAppCodes)
        && data.selectedRecommendedAppCodes.every((item) => typeof item === "string")
        ? data.selectedRecommendedAppCodes
        : null,
    };
    for (const field of selectionFields) {
      input[field] = typeof data[field] === "string" ? data[field] : null;
    }

    return {
      workspaceUuid: draft.workspaceUuid,
      workspaceName: draft.workspaceName.slice(0, 255),
      stage: draft.stage,
      setupStep: Number.isInteger(draft.setupStep) && Number(draft.setupStep) >= 0 ? Number(draft.setupStep) : 0,
      input,
    };
  } catch {
    return null;
  }
}

export function readIndustrySetupDraft(key: string): IndustrySetupDraft | null {
  try {
    return parseIndustrySetupDraft(window.localStorage.getItem(key));
  } catch {
    return null;
  }
}

export function writeIndustrySetupDraft(key: string, draft: IndustrySetupDraft): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Setup remains usable when browser storage is unavailable.
  }
}

export function removeIndustrySetupDraft(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Completing setup must not depend on browser storage.
  }
}
